/**
 * RoleBadge - Componente padronizado para exibir perfis de usuário
 *
 * Uso: Badge colorido e consistente para mostrar perfis em qualquer lugar da aplicação.
 * Sincronizado com constants/roles.ts (fonte única de verdade).
 *
 * @example
 * <RoleBadge role="MASTER" />
 * <RoleBadge role={user.role} size="lg" showIcon />
 */

import React from 'react';
import { ROLE_LABELS, ROLE_COLORS, isValidRole, type Role } from '../constants/roles';
import { Shield, UserCheck, ClipboardList, Users, Eye } from 'lucide-react';

interface RoleBadgeProps {
  /** Perfil do usuário (MASTER, AUDITOR, ANALISTA, CLIENTE, CONFERENTE) */
  role: string;
  /** Tamanho do badge */
  size?: 'sm' | 'md' | 'lg';
  /** Mostrar ícone do perfil */
  showIcon?: boolean;
  /** Classe CSS adicional */
  className?: string;
}

/**
 * Ícones por perfil
 */
const ROLE_ICONS: Record<Role, React.ReactNode> = {
  MASTER: <Shield className="w-3 h-3" />,
  AUDITOR: <UserCheck className="w-3 h-3" />,
  ANALISTA: <ClipboardList className="w-3 h-3" />,
  CLIENTE: <Users className="w-3 h-3" />,
  CONFERENTE: <Eye className="w-3 h-3" />,
};

/**
 * Tamanhos de badge
 */
const SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const RoleBadge: React.FC<RoleBadgeProps> = ({
  role,
  size = 'md',
  showIcon = false,
  className = '',
}) => {
  // Validar perfil
  if (!isValidRole(role)) {
    console.warn(`RoleBadge: Perfil inválido "${role}". Use: MASTER, AUDITOR, ANALISTA, CLIENTE, CONFERENTE`);
    return (
      <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        Perfil Inválido
      </span>
    );
  }

  const colors = ROLE_COLORS[role as Role];
  const label = ROLE_LABELS[role as Role];
  const icon = showIcon ? ROLE_ICONS[role as Role] : null;

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded font-semibold
        ${SIZE_CLASSES[size]}
        ${colors.bg} ${colors.text} ${colors.darkBg} ${colors.darkText}
        ${className}
      `.trim()}
      title={`Perfil: ${label}`}
    >
      {icon}
      {label}
    </span>
  );
};

/**
 * Badge com borda (para uso em cards, listagens)
 */
export const RoleBadgeOutlined: React.FC<RoleBadgeProps> = ({
  role,
  size = 'md',
  showIcon = false,
  className = '',
}) => {
  if (!isValidRole(role)) {
    return (
      <span className="px-2 py-1 rounded text-xs border border-gray-300 text-gray-600 dark:border-gray-700 dark:text-gray-400">
        Perfil Inválido
      </span>
    );
  }

  const colors = ROLE_COLORS[role as Role];
  const label = ROLE_LABELS[role as Role];
  const icon = showIcon ? ROLE_ICONS[role as Role] : null;

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded font-semibold border
        ${SIZE_CLASSES[size]}
        ${colors.bg} ${colors.text} ${colors.border}
        ${colors.darkBg} ${colors.darkText} ${colors.darkBorder}
        ${className}
      `.trim()}
      title={`Perfil: ${label}`}
    >
      {icon}
      {label}
    </span>
  );
};

export default RoleBadge;
